import { useChainId, useReadContracts } from 'wagmi'
import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { Address, zeroAddress } from 'viem'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { useIsolatedPairs } from '@/features/isolated/logic/useIsolatedPairs'
import { useAccount } from '@/domain/hooks/useAccount'
import { paths } from '@/config/paths'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { useMerklOpportunities } from '@/domain/merkl/opportunities/useOpportunities'
import { Opportunity } from '@merkl/api'
const SECONDS_PER_YEAR = 31_556_952

export interface UseMyPortfolioResults {
  deposits: (Deposit & { rowClickOptions: { destination: string } })[]
  borrows: (Borrow & { rowClickOptions: { destination: string } })[]
  guestMode: boolean
}

export interface Deposit {
  collateralToken: Token
  borrowToken: Token
  lendAmount: NormalizedUnitNumber
  supplyAPY: Percentage | undefined
  type: 'lending' | 'collateral'
  chainId: number
  pairAddress: Address
  merklOpportunity?: Opportunity
}

export interface Borrow {
  collateralToken: Token
  borrowToken: Token
  collateralAmount: NormalizedUnitNumber
  borrowAmount: NormalizedUnitNumber
  borrowAPY: Percentage | undefined
  chainId: number
  pairAddress: Address
}

export function useMyPortfolio(): UseMyPortfolioResults {
  const { pairs } = useIsolatedPairs()
  const { findByIdentifier } = useMerklOpportunities()
  const account = useAccount()
  const chainId = useChainId()

  // Get user snapshots for all pairs
  const userSnapshotReads =
    pairs?.map((pair) => ({
      address: pair.address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'getUserSnapshot',
      args: [account || zeroAddress],
    })) || []

  const { data: userSnapshots } = useReadContracts({
    contracts: userSnapshotReads,
  }) as { data: any[]; isLoading: boolean }

  // Get convertToAssets reads for all pairs with user asset shares
  const convertToAssetsReads =
    pairs
      ?.map((pair, index) => {
        const snapshot = userSnapshots?.[index]?.result as readonly [bigint, bigint, bigint] | undefined
        if (!snapshot) return null
        const [userAssetShares] = snapshot
        if (userAssetShares === 0n) return null
        return {
          address: pair.address,
          abi: FRAXLEND_PAIR_ABI,
          functionName: 'convertToAssets',
          args: [userAssetShares],
        }
      })
      .filter((read): read is NonNullable<typeof read> => read !== null) || []

  // Get toBorrowAmount reads for all pairs with user borrow shares
  const toBorrowAmountReads =
    pairs
      ?.map((pair, index) => {
        const snapshot = userSnapshots?.[index]?.result as readonly [bigint, bigint, bigint] | undefined
        if (!snapshot) return null
        const [, userBorrowShares] = snapshot
        if (userBorrowShares === 0n) return null
        return {
          address: pair.address,
          abi: FRAXLEND_PAIR_ABI,
          functionName: 'toBorrowAmount',
          args: [userBorrowShares, false, false], // roundUp=false, previewInterest=false
        }
      })
      .filter((read): read is NonNullable<typeof read> => read !== null) || []

  const { data: convertToAssetsResults } = useReadContracts({
    contracts: convertToAssetsReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: toBorrowAmountResults } = useReadContracts({
    contracts: toBorrowAmountReads,
  }) as { data: any[]; isLoading: boolean }

  const rateInfoReads =
    pairs?.map((pair) => ({
      address: pair.address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'currentRateInfo',
    })) || []

  const { data: rateInfos } = useReadContracts({
    contracts: rateInfoReads,
  }) as { data: any[]; isLoading: boolean }

  const deposits: Deposit[] = []
  const borrows: Borrow[] = []

  if (pairs && userSnapshots && rateInfos) {
    let convertToAssetsIndex = 0
    let toBorrowAmountIndex = 0

    pairs.forEach((pair, index) => {
      const snapshot = userSnapshots[index]?.result as readonly [bigint, bigint, bigint] | undefined
      const rateInfo = rateInfos[index]?.result as readonly [number, number, bigint, bigint, bigint] | undefined

      if (!snapshot || !rateInfo) return

      const [userAssetShares, userBorrowShares, userCollateralBalance] = snapshot
      const [_fee, _feeToProtocolRate, _borrowRate, ratePerSec, _lastBlock] = rateInfo

      const ratePerSecNum = Number(ratePerSec) / 1e18
      // feeToProtocolRate is 1e5 precision
      // see: https://github.com/FraxFinance/fraxlend-audit/blob/76c537d429ea4ac9bed2b13fc2cca90cccb2791a/src/contracts/FraxlendPairCore.sol#L103
      const feeToProtocolRate = Number(_feeToProtocolRate) / 1e5

      const borrowAPY = ratePerSecNum > 0 ? (Math.exp(ratePerSecNum * SECONDS_PER_YEAR) - 1) * 100 : 0

      const utilization = pair.utilization

      if (userAssetShares > 0n) {
        const lendAmount = convertToAssetsResults?.[convertToAssetsIndex]?.result as bigint | undefined
        convertToAssetsIndex++

        deposits.push({
          collateralToken: pair.borrowToken,
          borrowToken: pair.collateralToken,
          lendAmount: NormalizedUnitNumber(lendAmount ? Number(lendAmount) / 10 ** pair.borrowToken.decimals : 0),
          supplyAPY: Percentage((borrowAPY * (utilization / 100) * (1 - feeToProtocolRate)) / 100, true, true),
          type: 'lending',
          chainId,
          pairAddress: pair.address,
          merklOpportunity: findByIdentifier(CheckedAddress(pair.address)),
        })
      }

      if (userBorrowShares > 0n) {
        const borrowAmount = toBorrowAmountResults?.[toBorrowAmountIndex]?.result as bigint | undefined
        toBorrowAmountIndex++

        borrows.push({
          collateralToken: pair.collateralToken,
          borrowToken: pair.borrowToken,
          collateralAmount: NormalizedUnitNumber(Number(userCollateralBalance) / 10 ** pair.collateralToken.decimals),
          borrowAmount: NormalizedUnitNumber(borrowAmount ? Number(borrowAmount) / 10 ** pair.borrowToken.decimals : 0),
          borrowAPY: Percentage(borrowAPY / 100, true, true),
          chainId,
          pairAddress: pair.address,
        })
      }

      if (userCollateralBalance > 0n) {
        deposits.push({
          collateralToken: pair.collateralToken,
          borrowToken: pair.borrowToken,
          lendAmount: NormalizedUnitNumber(Number(userCollateralBalance) / 10 ** pair.collateralToken.decimals),
          supplyAPY: Percentage(0, true, true),
          type: 'collateral',
          chainId,
          pairAddress: pair.address,
          merklOpportunity: findByIdentifier(CheckedAddress(pair.address)),
        })
      }
    })
  }

  return {
    deposits: deposits.map((deposit) => ({
      ...deposit,
      rowClickOptions: getRowClickOptions(chainId, deposit.pairAddress),
    })),
    borrows: borrows.map((borrow) => ({
      ...borrow,
      rowClickOptions: getRowClickOptions(chainId, borrow.pairAddress),
    })),
    guestMode: !account,
  }
}

function getRowClickOptions(chainId: number, pairAddress: Address): { destination: string } {
  return {
    destination: paths.isolatedDetails.replace(':chainId', chainId.toString()).replace(':pairAddress', pairAddress),
  }
}
